import React, {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useResponsive } from '../hooks/useResponsive';
import { LayerBoundary } from '../providers/LayerProvider';
import { useThemeColors } from '../providers/ThemeProvider';
import {
	clampDockTabLeft,
	computeDockRightOffsets,
	DOCK_EDGE_MARGIN_PX,
	dockBottomOffset,
	dockMaxHeight,
	isNearDockRow,
	overlayWindowZIndex,
} from './overlayDock';
import { OverlayService, OverlayState } from './OverlayService';

type ResizeDirection =
	| 'right'
	| 'bottom'
	| 'corner'
	// Mirrored edges for a window anchored at the bottom-right (docked):
	// it grows up and to the left, so those are the edges that can follow
	// the cursor.
	| 'left'
	| 'top'
	| 'top-left';

/** Fallback width for a docked window that did not pass a numeric `width` prop. */
const DOCK_DEFAULT_WIDTH_PX = 400;

interface OverlayContextType {
	overlayService: OverlayService;
	overlays: OverlayState[];
}

/**
 * Overlay props are an untyped `Record<string, unknown>`; these helpers narrow
 * the handful of style/title values this renderer reads to what callers
 * actually store (CSS sizes as strings like '800px' or numbers like 410).
 */
const propAsCssSize = (value: unknown): string | number | undefined =>
	typeof value === 'string' || typeof value === 'number' ? value : undefined;

const propAsString = (value: unknown): string | undefined =>
	typeof value === 'string' ? value : undefined;

/**
 * Mirrors the legacy `parseInt(props.x || fallback)` coercion: numeric props
 * pass through truncated, px strings are parsed, anything else falls back.
 */
const propAsPx = (value: unknown, fallback: number): number => {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return Math.trunc(value);
	}
	if (typeof value === 'string' && value !== '') {
		const parsed = parseInt(value);
		if (!Number.isNaN(parsed)) {
			return parsed;
		}
	}
	return fallback;
};

const OverlayContext = createContext<OverlayContextType | null>(null);

export const useOverlay = (): OverlayContextType => {
	const context = useContext(OverlayContext);
	if (!context) {
		throw new Error('useOverlay must be used within an OverlayProvider');
	}
	return context;
};

interface OverlayProviderProps {
	children: ReactNode;
}

export const OverlayProvider: React.FC<OverlayProviderProps> = ({
	children,
}) => {
	const [overlays, setOverlays] = useState<OverlayState[]>([]);
	const overlayService = OverlayService.getInstance();

	useEffect(() => {
		const unsubscribe = overlayService.subscribe(setOverlays);
		return unsubscribe;
	}, [overlayService]);

	return (
		<OverlayContext.Provider value={{ overlayService, overlays }}>
			{children}
		</OverlayContext.Provider>
	);
};

/** The width a docked window occupies in the dock row. */
const dockWidthOf = (overlay: OverlayState): number =>
	overlay.size?.width ??
	propAsPx(overlay.props?.['width'], DOCK_DEFAULT_WIDTH_PX);

// Individual overlay renderer component
const SingleOverlayRenderer: React.FC<{
	overlay: OverlayState;
	/** Distance from the viewport's right edge while docked in the row. */
	dockRight?: number;
	/** Space reserved at the bottom of the screen for a full-width bar. */
	dockBottomInset: number;
}> = ({ overlay, dockRight, dockBottomInset }) => {
	const overlayService = OverlayService.getInstance();
	const colors = useThemeColors();
	const {
		isMobile,
		width: viewportWidth,
		height: viewportHeight,
	} = useResponsive();
	const [mounted, setMounted] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [dragStartY, setDragStartY] = useState(0);
	const [currentY, setCurrentY] = useState(0);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeDirection, setResizeDirection] =
		useState<ResizeDirection | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	// While a docked window is dragged within snapping distance of the dock
	// row: its height, for the outline showing where it will land if dropped.
	const [dockSnapHeight, setDockSnapHeight] = useState<number | null>(null);

	// Helper function to constrain position within viewport
	const constrainToViewport = (
		x: number,
		y: number,
		width: number | string = 400,
		height: number | string = 48,
	) => {
		// Parse width/height if they're strings
		const windowWidth =
			typeof width === 'string' ? parseInt(width) || 800 : width;
		const windowHeight =
			typeof height === 'string' ? parseInt(height) || 600 : height;

		const minVisible = 50; // Minimum pixels that must remain visible

		// Calculate bounds - ensure window stays accessible
		// For X: ensure at least minVisible pixels are shown on either side
		const minX = -windowWidth + minVisible; // Can go left until only 50px is visible
		const maxX = window.innerWidth - minVisible; // Can go right until only 50px is visible

		// For Y: ensure header stays fully visible at top, and at least 50px visible at bottom
		const minY = 0; // Can't go above viewport
		const maxY = window.innerHeight - minVisible; // Can go down until only 50px is visible

		return {
			x: Math.max(minX, Math.min(maxX, x)),
			y: Math.max(minY, Math.min(maxY, y)),
		};
	};

	// Determine if this should be a bottom sheet (moved up to be available for all effects)
	const actualPosition =
		overlay.position === 'auto'
			? overlay.responsive && isMobile
				? 'bottom'
				: 'floating'
			: overlay.position;
	const isBottomSheet = actualPosition === 'bottom';
	// PD-14656 — `dock` windows (the chat pop-outs) live in a row along the
	// bottom-right corner rather than at a remembered top/left. Desktop
	// floating layout only; on a phone the same overlay is a bottom sheet.
	const isDocked =
		overlay.dock === 'bottom-right' && actualPosition === 'floating';
	// Until the user drags it, a docked window is anchored by right/bottom,
	// so it follows a browser resize with no bookkeeping. Once dragged it
	// keeps the spot it was dropped on (top/left, like any floating window)
	// and the rest of the row closes up behind it.
	const isDockAnchored = isDocked && overlay.overlayPosition == null;
	const isDockedTab = isDocked && overlay.isMinimized === true;
	const titleProp = propAsString(overlay.props?.['title']);
	const overlayTitle =
		titleProp != null && titleProp !== '' ? titleProp : 'Window';
	// Resize handles, on whichever edges can follow the cursor: a docked
	// window is anchored at the bottom-right and grows up and to the left,
	// so it gets top/left handles instead of the usual right/bottom ones.
	const canResize =
		overlay.resizable === true &&
		!isBottomSheet &&
		overlay.isMinimized !== true;
	const showDockResizeHandles = canResize && isDockAnchored;
	const showStandardResizeHandles = canResize && !isDockAnchored;

	useEffect(() => {
		setMounted(true);
	}, []);

	// Handle window resize to ensure overlay stays within bounds
	useEffect(() => {
		if (!overlay.draggable || isBottomSheet || overlay.position === 'center')
			return;

		const handleResize = () => {
			if (overlay.overlayPosition) {
				const width = propAsCssSize(overlay.props?.['width']) || '800px';
				const windowWidth =
					typeof width === 'string' ? parseInt(width) || 800 : width;

				// Only constrain if window is completely out of view
				const { x, y } = overlay.overlayPosition;
				let needsUpdate = false;
				let newX = x;
				let newY = y;

				// Check if window is completely out of view horizontally
				if (x > window.innerWidth) {
					newX = window.innerWidth - 50; // Bring back with 50px visible
					needsUpdate = true;
				} else if (x + windowWidth < 0) {
					newX = -windowWidth + 50; // Bring back with 50px visible
					needsUpdate = true;
				}

				// Check if window is completely out of view vertically
				if (y < 0) {
					newY = 0;
					needsUpdate = true;
				} else if (y > window.innerHeight - 50) {
					newY = window.innerHeight - 50;
					needsUpdate = true;
				}

				if (needsUpdate) {
					overlayService.setPosition(overlay.id, { x: newX, y: newY });
				}
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [
		overlay.id,
		overlay.overlayPosition,
		overlay.draggable,
		overlay.isMinimized,
		isBottomSheet,
		overlay.position,
	]);

	const overlayProps = {
		...overlay.props,
		overlay: overlay,
		onClose: () => overlayService.hide(overlay.id),
		onMinimize: overlay.minimizable
			? () => overlayService.toggleMinimize(overlay.id)
			: undefined,
		onBringToFront: () => overlayService.bringToFront(overlay.id),
		isMinimized: overlay.isMinimized,
		isBottomSheet: isBottomSheet,
		isCollapsed: isCollapsed,
		onCollapse: () => setIsCollapsed(true),
		onExpand: () => setIsCollapsed(false),
		isInDraggableWindow:
			overlay.draggable && !isBottomSheet && actualPosition === 'floating',
	};

	// Get position styles based on overlay position
	const getPositionStyles = () => {
		if (isBottomSheet) {
			return {
				bottom: 0,
				left: 0,
				right: 0,
				transform: isCollapsed
					? 'translateY(calc(100% - 80px))'
					: `translateY(${currentY}px)`,
				transition: isDragging ? 'none' : 'transform 0.3s ease-out',
			};
		}

		switch (actualPosition) {
			case 'center':
				return {
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
				};
			case 'top':
				return {
					top: '20px',
					left: '50%',
					transform: 'translateX(-50%)',
				};
			case 'floating':
			case 'custom': {
				// 'bottom' is unreachable here (isBottomSheet returned above) and
				// 'auto' is always resolved to 'bottom'/'floating' before this point.
				if (isDocked) {
					const bottom = dockBottomOffset(
						overlay.isMinimized === true,
						dockBottomInset,
					);
					const placed = overlay.overlayPosition;
					if (placed == null) {
						return {
							bottom,
							right: dockRight ?? DOCK_EDGE_MARGIN_PX,
						};
					}
					// A dragged docked window minimizes to a tab on the bottom
					// edge under where it was, and restores to the exact spot
					// it was dragged to, because that position is untouched.
					if (overlay.isMinimized === true) {
						return {
							bottom,
							left: clampDockTabLeft(
								placed.x,
								dockWidthOf(overlay),
								viewportWidth,
							),
						};
					}
				}
				// Use the position directly without constraining here to avoid jumps
				// Constraints are applied during drag operations
				// Use nullish coalescing (??) instead of || to allow 0 as a valid position
				return {
					top: overlay.overlayPosition?.y ?? 100,
					left: overlay.overlayPosition?.x ?? 100,
				};
			}
			default:
				return {};
		}
	};

	// Add drag event listeners
	useEffect(() => {
		if (isDragging) {
			const handleMove = (e: MouseEvent | TouchEvent) => handleDragMove(e);
			const handleEnd = () => handleDragEnd();

			document.addEventListener('mousemove', handleMove);
			document.addEventListener('touchmove', handleMove);
			document.addEventListener('mouseup', handleEnd);
			document.addEventListener('touchend', handleEnd);

			return () => {
				document.removeEventListener('mousemove', handleMove);
				document.removeEventListener('touchmove', handleMove);
				document.removeEventListener('mouseup', handleEnd);
				document.removeEventListener('touchend', handleEnd);
			};
		}
		return undefined;
	}, [isDragging, dragStartY, currentY, isBottomSheet]);

	// Handle touch/mouse drag for bottom sheet
	const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
		if (!isBottomSheet) return;

		// Prevent text selection during drag
		e.preventDefault();

		// A touchstart always carries at least one touch; the guard is for TS
		const startPoint = 'touches' in e ? e.touches[0] : e;
		if (startPoint == null) return;
		const startY = startPoint.clientY;
		setDragStartY(startY);
		setIsDragging(true);

		// Disable text selection globally
		document.body.style.userSelect = 'none';
		document.body.style.webkitUserSelect = 'none';
	};

	const handleDragMove = (e: MouseEvent | TouchEvent) => {
		if (!isDragging || !isBottomSheet) return;

		const movePoint = 'touches' in e ? e.touches[0] : e;
		if (movePoint == null) return;
		const currentPos = movePoint.clientY;
		const deltaY = currentPos - dragStartY;

		// Only allow dragging down (positive deltaY)
		if (deltaY > 0) {
			setCurrentY(deltaY);
		}
	};

	const handleDragEnd = () => {
		if (!isDragging || !isBottomSheet) return;

		setIsDragging(false);

		// Re-enable text selection
		document.body.style.userSelect = '';
		document.body.style.webkitUserSelect = '';

		// If dragged more than 100px or 30% of height, collapse
		const threshold = Math.min(100, window.innerHeight * 0.3);

		if (currentY > threshold) {
			setIsCollapsed(true);
		}

		setCurrentY(0);
		setDragStartY(0);
	};

	// Handle resize start
	const handleResizeStart = (
		e: React.MouseEvent,
		direction: ResizeDirection,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setIsResizing(true);
		setResizeDirection(direction);

		// Disable text selection during resize
		document.body.style.userSelect = 'none';
		document.body.style.webkitUserSelect = 'none';

		const startX = e.clientX;
		const startY = e.clientY;
		const startWidth =
			overlay.size?.width || propAsPx(overlay.props?.['width'], 800);
		const startHeight =
			overlay.size?.height || propAsPx(overlay.props?.['height'], 600);

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - startX;
			const deltaY = e.clientY - startY;

			let newWidth = startWidth;
			let newHeight = startHeight;

			if (direction === 'right' || direction === 'corner') {
				newWidth = startWidth + deltaX;
			}
			if (direction === 'left' || direction === 'top-left') {
				newWidth = startWidth - deltaX;
			}

			if (direction === 'bottom' || direction === 'corner') {
				newHeight = startHeight + deltaY;
			}
			if (direction === 'top' || direction === 'top-left') {
				newHeight = startHeight - deltaY;
			}

			// Apply minimum constraints
			const minWidth = propAsPx(overlay.props?.['minWidth'], 300);
			const minHeight = propAsPx(overlay.props?.['minHeight'], 200);

			newWidth = Math.max(minWidth, newWidth);
			newHeight = Math.max(minHeight, newHeight);

			// Apply maximum constraints (90% of viewport). A docked window grows
			// upward from its bottom anchor, so it also stops short of pushing
			// its title bar off the top of the screen.
			const maxWidth = window.innerWidth * 0.9;
			const maxHeight = isDockAnchored
				? dockMaxHeight(window.innerHeight, dockBottomInset)
				: window.innerHeight * 0.9;

			newWidth = Math.min(maxWidth, newWidth);
			newHeight = Math.min(maxHeight, newHeight);

			overlayService.setSize(overlay.id, {
				width: newWidth,
				height: newHeight,
			});
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			setResizeDirection(null);

			// Re-enable text selection
			document.body.style.userSelect = '';
			document.body.style.webkitUserSelect = '';

			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	// Early return check - after all hooks
	if (!overlay.isVisible || !mounted) {
		return null;
	}

	// OverlayState erases each registered component's props to `unknown`; this
	// renderer injects the standard overlay control props (onClose, onMinimize,
	// …), which the type system can't associate back to the erased component.
	const Component = overlay.component as React.ComponentType<
		Record<string, unknown>
	>;

	// OverlayService ranks the visible windows from Z_INDEX.modal upwards; a
	// window in the dock row instead sits under sidebars and the navigation
	// dropdowns (see overlayWindowZIndex). Everything rendered in the window
	// (its selects, menus, tooltips and popovers) reads this through the
	// LayerBoundary below, so a menu opened in a window opens above it rather
	// than underneath (PD-16154).
	const windowZIndex = overlayWindowZIndex(overlay.zIndex, isDockAnchored);

	const content = (
		<LayerBoundary zIndex={windowZIndex}>
			{/* Backdrop for modal overlays */}
			{overlay.backdrop && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: `rgba(0, 0, 0, ${overlay.backdropOpacity || 0.5})`,
						zIndex: windowZIndex - 1,
					}}
					onClick={() => overlayService.handleBackdropPress(overlay.id)}
				/>
			)}

			{/* Where a docked window dragged near the dock row will land if dropped */}
			{dockSnapHeight != null &&
				overlay.dockSnapPending === true &&
				dockRight != null && (
					<div
						aria-hidden
						className="pointer-events-none rounded-lg border-2 border-dashed border-accent bg-accent-badge"
						style={{
							position: 'fixed',
							right: dockRight,
							bottom: dockBottomOffset(false, dockBottomInset),
							width: dockWidthOf(overlay),
							height: Math.min(
								dockSnapHeight,
								dockMaxHeight(viewportHeight, dockBottomInset),
							),
							zIndex: windowZIndex - 1,
						}}
					/>
				)}

			{/* Overlay Container */}
			<div
				ref={containerRef}
				style={{
					position: 'fixed',
					zIndex: windowZIndex,
					...getPositionStyles(),
				}}
				className={`overlay-container ${!isBottomSheet ? (overlay.animation || 'fade') + '-animation' : ''}`}
			>
				<div
					className={`bg-white dark:bg-surface shadow-xl overflow-hidden flex flex-col ${
						isBottomSheet
							? 'rounded-t-2xl w-full'
							: // A minimized docked window is a tab sitting flush on
								// the bottom edge, so only its top corners round.
								isDockedTab
								? 'rounded-t-lg'
								: 'rounded-lg'
					} ${overlay.resizable && !isBottomSheet && !overlay.isMinimized ? 'relative' : ''}`}
					style={{
						width: isBottomSheet
							? '100%'
							: overlay.size?.width ||
								propAsCssSize(overlay.props?.['width']) ||
								'auto',
						height: isBottomSheet
							? isCollapsed
								? '80px'
								: '70vh'
							: overlay.isMinimized
								? '48px' // Just header height when minimized
								: overlay.size?.height ||
									propAsCssSize(overlay.props?.['height']) ||
									'auto',
						maxWidth: isBottomSheet ? '100%' : '90vw',
						maxHeight: isBottomSheet
							? '70vh'
							: isDockAnchored
								? dockMaxHeight(viewportHeight, dockBottomInset)
								: '90vh',
						minWidth: propAsCssSize(overlay.props?.['minWidth']) || '300px',
						minHeight: overlay.isMinimized
							? '48px'
							: propAsCssSize(overlay.props?.['minHeight']) || '200px',
					}}
				>
					{/* Bottom Sheet Drag Handle */}
					{isBottomSheet && (
						<div
							className="bg-subtle px-4 py-2 cursor-grab active:cursor-grabbing"
							onMouseDown={handleDragStart}
							onTouchStart={handleDragStart}
							onClick={() => isCollapsed && setIsCollapsed(false)}
						>
							<div className="w-10 h-1 bg-neutral-solid rounded-full mx-auto mb-2" />
							<div className="flex items-center justify-between">
								<span className="text-sm font-medium text-muted">
									{propAsString(overlay.props?.['title']) || 'AI Assistant'}
								</span>
								<button
									onClick={(e) => {
										e.stopPropagation();
										overlayService.hide(overlay.id);
									}}
									className="text-subtle hover:text-strong"
								>
									<svg
										className="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</div>
						</div>
					)}

					{/* Header with drag handle for floating overlays */}
					{overlay.draggable &&
						!isBottomSheet &&
						actualPosition === 'floating' &&
						overlay.showModalHeader !== false && (
							<div
								className={`bg-subtle px-4 py-2 flex items-center justify-between ${
									isDockedTab ? '' : 'cursor-move'
								}`}
								onMouseDown={(e) => {
									// Prevent drag on buttons
									if ((e.target as HTMLElement).closest('button')) return;
									// A minimized docked window is a tab parked on the
									// bottom edge; it restores rather than drags, so the
									// row keeps its shape and the tab never floats.
									if (isDockedTab) {
										return;
									}

									// Prevent text selection during drag
									e.preventDefault();

									// Simple drag implementation with viewport constraints.
									// A docked window is placed by right/bottom and has no
									// stored top/left yet, so its drag starts from where it
									// is actually drawn; the stored-position fallback
									// would jump it to 100,100 on the first move.
									const dockedRect = isDocked
										? containerRef.current?.getBoundingClientRect()
										: undefined;
									// Undocked windows keep the original `|| 100` fallback,
									// which also treats a stored 0 as unset.
									const storedX = overlay.overlayPosition?.x ?? 0;
									const storedY = overlay.overlayPosition?.y ?? 0;
									const startX =
										dockedRect != null
											? e.clientX - dockedRect.left
											: e.clientX - (storedX !== 0 ? storedX : 100);
									const startY =
										dockedRect != null
											? e.clientY - dockedRect.top
											: e.clientY - (storedY !== 0 ? storedY : 100);

									// Add class to body to prevent text selection globally during drag
									document.body.style.userSelect = 'none';
									document.body.style.webkitUserSelect = 'none';

									// A docked window dropped with its bottom edge near the
									// dock row goes back into it (PD-14656), so the drag
									// tracks whether it is in snapping distance.
									const windowHeight =
										containerRef.current?.getBoundingClientRect().height ?? 0;
									let dropsIntoDock = false;

									const handleMouseMove = (e: MouseEvent) => {
										const newX = e.clientX - startX;
										const newY = e.clientY - startY;

										// Get window dimensions for constraint calculation
										const width =
											propAsCssSize(overlay.props?.['width']) || '800px';
										const height = overlay.isMinimized
											? '48px'
											: propAsCssSize(overlay.props?.['height']) || '600px';

										const constrained = constrainToViewport(
											newX,
											newY,
											width,
											height,
										);

										overlayService.setPosition(overlay.id, constrained);

										if (isDocked) {
											const near = isNearDockRow(
												constrained.y + windowHeight,
												window.innerHeight,
												dockBottomInset,
											);
											if (near !== dropsIntoDock) {
												dropsIntoDock = near;
												setDockSnapHeight(near ? windowHeight : null);
												overlayService.setDockSnapPending(overlay.id, near);
											}
										}
									};

									const handleMouseUp = () => {
										// Re-enable text selection
										document.body.style.userSelect = '';
										document.body.style.webkitUserSelect = '';

										if (dropsIntoDock) {
											setDockSnapHeight(null);
											overlayService.returnToDock(overlay.id);
										}

										document.removeEventListener('mousemove', handleMouseMove);
										document.removeEventListener('mouseup', handleMouseUp);
									};

									document.addEventListener('mousemove', handleMouseMove);
									document.addEventListener('mouseup', handleMouseUp);
								}}
							>
								{isDockedTab ? (
									// The whole title of a docked tab restores it, the way
									// minimized chat tabs work elsewhere.
									<button
										type="button"
										onClick={() => {
											overlayService.toggleMinimize(overlay.id);
										}}
										className="min-w-0 flex-1 cursor-pointer truncate text-left text-sm font-medium text-muted"
									>
										{overlayTitle}
									</button>
								) : (
									<span className="text-sm font-medium text-muted">
										{overlayTitle}
									</span>
								)}
								<div className="flex items-center space-x-2">
									{overlay.minimizable && (
										<button
											onClick={() => overlayService.toggleMinimize(overlay.id)}
											className="text-subtle hover:text-strong"
										>
											<svg
												className="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M20 12H4"
												/>
											</svg>
										</button>
									)}
									<button
										onClick={() => overlayService.hide(overlay.id)}
										className="text-subtle hover:text-strong"
									>
										<svg
											className="w-4 h-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									</button>
								</div>
							</div>
						)}

					{/* Content - Hide when collapsed on mobile or minimized on desktop */}
					{(!isBottomSheet || !isCollapsed) && (
						<div
							className="p-4 overflow-hidden flex flex-col"
							style={{
								height: isBottomSheet
									? 'calc(100% - 48px)'
									: overlay.draggable &&
										  !isBottomSheet &&
										  actualPosition === 'floating'
										? 'calc(100% - 48px)'
										: '100%',
								display: overlay.isMinimized ? 'none' : 'flex',
							}}
						>
							<Component {...overlayProps} />
						</div>
					)}

					{/* Resize handles for a docked window (top and left edges) */}
					{showDockResizeHandles && (
						<>
							{/* Left edge */}
							<div
								className="absolute top-0 left-0 w-1 h-full cursor-ew-resize hover:bg-primary-300 dark:hover:bg-primary-700 transition-colors"
								onMouseDown={(e) => {
									handleResizeStart(e, 'left');
								}}
							/>
							{/* Top edge */}
							<div
								className="absolute top-0 left-0 w-full h-1 cursor-ns-resize hover:bg-primary-300 dark:hover:bg-primary-700 transition-colors"
								onMouseDown={(e) => {
									handleResizeStart(e, 'top');
								}}
							/>
							{/* Top-left corner */}
							<div
								className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize hover:bg-primary-300 dark:hover:bg-primary-700 transition-colors"
								onMouseDown={(e) => {
									handleResizeStart(e, 'top-left');
								}}
							/>
						</>
					)}

					{/* Resize Handles */}
					{showStandardResizeHandles && (
						<>
							{/* Right edge */}
							<div
								className="absolute top-0 right-0 w-1 h-full cursor-ew-resize hover:bg-primary-300 dark:hover:bg-primary-700 transition-colors"
								onMouseDown={(e) => handleResizeStart(e, 'right')}
							/>
							{/* Bottom edge */}
							<div
								className="absolute bottom-0 left-0 w-full h-1 cursor-ns-resize hover:bg-primary-300 dark:hover:bg-primary-700 transition-colors"
								onMouseDown={(e) => handleResizeStart(e, 'bottom')}
							/>
							{/* Bottom-right corner */}
							<div
								className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize hover:bg-primary-300 dark:hover:bg-primary-700 transition-colors"
								onMouseDown={(e) => handleResizeStart(e, 'corner')}
							/>
						</>
					)}
				</div>
			</div>
		</LayerBoundary>
	);

	// Render in portal
	return ReactDOM.createPortal(content, document.body);
};

// Main overlay renderer that renders all overlays
export const OverlayRenderer: React.FC = () => {
	const { overlays, overlayService } = useOverlay();
	const { width: viewportWidth } = useResponsive();

	// PD-14656 — docked windows form one row, so each one's spot depends on
	// the others. Work the row out once here from the windows docked right
	// now (visible, and not dragged out of the row) and hand each renderer
	// its own offset. Recomputed on every overlay change and every browser
	// resize, which is what closes gaps and keeps the row on-screen.
	const dockRightOffsets = useMemo(
		() =>
			computeDockRightOffsets(
				overlays
					.filter(
						(overlay) =>
							overlay.dock === 'bottom-right' &&
							// A window dragged back within snapping distance keeps
							// its place, so the others do not slide under the
							// outline showing where it will land.
							(overlay.overlayPosition == null ||
								overlay.dockSnapPending === true),
					)
					.map((overlay) => ({
						id: overlay.id,
						width: dockWidthOf(overlay),
						sequence: overlay.dockSequence ?? 0,
					})),
				viewportWidth,
			),
		[overlays, viewportWidth],
	);
	// Read on every render: a change to the reserved space notifies the same
	// listeners as an overlay change, so this re-renders when it moves.
	const dockBottomInset = overlayService.getDockBottomInset();

	return (
		<>
			{overlays.map((overlay) => (
				<SingleOverlayRenderer
					key={overlay.id}
					overlay={overlay}
					dockRight={dockRightOffsets.get(overlay.id)}
					dockBottomInset={dockBottomInset}
				/>
			))}
		</>
	);
};

// CSS animations (should be in a global CSS file)
const globalStyles = `
  .fade-animation {
    animation: fadeIn 0.2s ease-in-out;
  }
  
  .scale-animation {
    animation: scaleIn 0.2s ease-in-out;
  }
  
  .slide-animation {
    animation: slideIn 0.3s ease-in-out;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes scaleIn {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  
  @keyframes slideIn {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

// Inject styles on mount
if (
	typeof document !== 'undefined' &&
	!document.getElementById('overlay-styles')
) {
	const style = document.createElement('style');
	style.id = 'overlay-styles';
	style.textContent = globalStyles;
	document.head.appendChild(style);
}
